import { useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { XIcon, FilePdfIcon, ArrowCounterClockwiseIcon } from "@phosphor-icons/react";

export default function ReservationModal({ car, onClose, onSaved }) {
  const [form, setForm] = useState({
    status: car.status || "reserved",
    customer_name: car.customer_name || "",
    customer_phone: car.customer_phone || "",
    customer_email: car.customer_email || "",
    customer_address: car.customer_address || "",
    customer_id_number: car.customer_id_number || "",
    customer_afm: car.customer_afm || "",
    sale_date: car.sale_date || new Date().toLocaleDateString("el-GR"),
    deposit_eur: car.deposit_eur || 0,
    final_price_eur: car.final_price_eur || car.price_eur || 0,
    customer_notes: car.customer_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [showRelease, setShowRelease] = useState(false);
  const [releaseNote, setReleaseNote] = useState("");
  const [releasing, setReleasing] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    e?.preventDefault();
    if (!form.customer_name.trim()) {
      toast.error("Συμπληρώστε το ονοματεπώνυμο πελάτη");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        deposit_eur: Number(form.deposit_eur) || 0,
        final_price_eur: Number(form.final_price_eur) || 0,
      };
      const r = await api.put(`/cars/${car.id}`, payload);
      toast.success(form.status === "sold" ? "Πώληση καταχωρήθηκε" : "Κράτηση καταχωρήθηκε");
      onSaved(r.data);
    } catch (e) {
      toast.error("Αποτυχία αποθήκευσης");
    } finally {
      setSaving(false);
    }
  };

  const release = async () => {
    if (!releaseNote.trim()) {
      toast.error("Απαιτείται σχόλιο για την ακύρωση");
      return;
    }
    setReleasing(true);
    try {
      const r = await api.post(`/cars/${car.id}/status`, {
        status: "available",
        note: releaseNote.trim(),
        keep_customer: false,
      });
      toast.success("Το αυτοκίνητο είναι ξανά διαθέσιμο");
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία");
    } finally {
      setReleasing(false);
    }
  };

  const isReservedOrSold = car.status === "reserved" || car.status === "sold";

  const downloadPDF = async () => {
    try {
      // Auth via httpOnly cookie (api instance has withCredentials: true)
      const r = await api.get(`/cars/${car.id}/purchase-document.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `purchase-${car.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Λήψη PDF");
    } catch {
      toast.error("Αποθηκεύστε πρώτα τα στοιχεία πελάτη");
    }
  };

  return createPortal((
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <form onSubmit={save} className="bg-white border border-black/10 w-full max-w-2xl max-h-[90vh] overflow-auto" data-testid="reservation-modal">
        <div className="px-5 py-3 border-b border-black/10 bg-[#0A0A0A] text-white flex items-center justify-between sticky top-0 z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">Κράτηση / Πώληση</div>
            <h3 className="heading font-bold">{car.brand} {car.model} {car.variant || ""}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-white/10" data-testid="reservation-close">
            <XIcon size={18} />
          </button>
        </div>

        {showRelease ? (
          <div className="p-5 space-y-4" data-testid="release-form">
            <div className="bg-[#FEF3C7] border border-[#F59E0B] p-3 text-sm text-[#92400E]">
              <strong>Ακύρωση κράτησης / Επαναφορά σε διαθέσιμο.</strong> Τα στοιχεία του πελάτη θα διαγραφούν αλλά θα κρατηθούν στο ιστορικό του οχήματος.
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#4B5563]">Σχόλιο ακύρωσης (απαραίτητο)</span>
              <textarea
                value={releaseNote}
                onChange={(e) => setReleaseNote(e.target.value)}
                rows={4}
                placeholder="π.χ. Ο πελάτης άλλαξε γνώμη, δεν εγκρίθηκε χρηματοδότηση, βρήκε άλλο αυτοκίνητο..."
                data-testid="release-note"
                className="w-full px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowRelease(false)} className="btn-secondary">Πίσω</button>
              <button
                type="button"
                onClick={release}
                disabled={releasing || !releaseNote.trim()}
                data-testid="release-confirm"
                className="bg-[#E60000] hover:bg-[#CC0000] text-white px-4 py-2 text-sm flex items-center gap-1.5 disabled:opacity-50"
              >
                <ArrowCounterClockwiseIcon size={14} weight="bold" />
                {releasing ? "Επεξεργασία..." : "Επαναφορά σε Διαθέσιμο"}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Status */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "available", label: "Διαθέσιμο", color: "bg-[#16A34A]" },
                { v: "reserved", label: "Κρατημένο", color: "bg-[#F59E0B]" },
                { v: "sold", label: "Πωλημένο", color: "bg-[#0A0A0A]" },
              ].map((s) => (
                <button
                  type="button"
                  key={s.v}
                  onClick={() => set("status", s.v)}
                  data-testid={`status-${s.v}`}
                  className={`px-3 py-3 text-sm border transition-all ${
                    form.status === s.v
                      ? `${s.color} text-white border-transparent`
                      : "bg-white border-black/20 hover:bg-[#F9FAFB]"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {isReservedOrSold && (
              <button
                type="button"
                onClick={() => setShowRelease(true)}
                data-testid="release-btn"
                className="w-full border-2 border-dashed border-[#E60000] text-[#E60000] hover:bg-[#FEF2F2] px-3 py-2 text-sm flex items-center justify-center gap-2"
              >
                <ArrowCounterClockwiseIcon size={16} weight="bold" />
                Ακύρωση κράτησης · επαναφορά σε «Ελεύθερο»
              </button>
            )}

            {/* Customer fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="Ονοματεπώνυμο *">
                <input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} required className="w-full px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm" data-testid="cust-name-input" />
              </FormField>
              <FormField label="Τηλέφωνο">
                <input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} className="w-full px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm font-mono" />
              </FormField>
              <FormField label="Email">
                <input type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} className="w-full px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm" />
              </FormField>
              <FormField label="Α.Δ.Τ. / Διαβατήριο">
                <input value={form.customer_id_number} onChange={(e) => set("customer_id_number", e.target.value)} className="w-full px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm font-mono" />
              </FormField>
              <FormField label="Διεύθυνση">
                <input value={form.customer_address} onChange={(e) => set("customer_address", e.target.value)} className="w-full px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm" />
              </FormField>
              <FormField label="ΑΦΜ">
                <input value={form.customer_afm} onChange={(e) => set("customer_afm", e.target.value)} className="w-full px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm font-mono" />
              </FormField>
            </div>

            {/* Financial */}
            <div className="grid grid-cols-3 gap-3 pt-2 border-t border-black/10">
              <FormField label="Τελική Τιμή (€)">
                <input type="number" value={form.final_price_eur} onChange={(e) => set("final_price_eur", e.target.value)} className="w-full px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm font-mono" />
              </FormField>
              <FormField label="Προκαταβολή (€)">
                <input type="number" value={form.deposit_eur} onChange={(e) => set("deposit_eur", e.target.value)} className="w-full px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm font-mono" />
              </FormField>
              <FormField label="Ημερομηνία">
                <input value={form.sale_date} onChange={(e) => set("sale_date", e.target.value)} placeholder="14/05/2026" className="w-full px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm font-mono" />
              </FormField>
            </div>

            <FormField label="Σημειώσεις">
              <textarea value={form.customer_notes} onChange={(e) => set("customer_notes", e.target.value)} rows={3} className="w-full px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm" />
            </FormField>

            <div className="bg-[#F9FAFB] border border-black/10 p-3 flex justify-between items-center">
              <span className="text-sm text-[#4B5563]">Υπόλοιπο προς εξόφληση:</span>
              <span className="heading text-2xl font-extrabold font-mono">
                {Math.max(0, (Number(form.final_price_eur) || 0) - (Number(form.deposit_eur) || 0)).toLocaleString("el-GR")} €
              </span>
            </div>
          </div>
        )}

        {!showRelease && (
          <div className="px-5 py-3 border-t border-black/10 bg-[#F9FAFB] sticky bottom-0 flex flex-wrap justify-between gap-2">
            <button
              type="button"
              onClick={downloadPDF}
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white px-4 py-2 flex items-center gap-1.5 text-sm"
            >
              <FilePdfIcon size={16} weight="fill" /> Λήψη Εγγράφου PDF
            </button>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="btn-secondary">Ακύρωση</button>
              <button type="submit" disabled={saving} className="btn-primary" data-testid="reservation-save">
                {saving ? "Αποθήκευση..." : "Αποθήκευση"}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  ), document.body);
}

function FormField({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-[0.15em] text-[#4B5563]">{label}</span>
      {children}
    </label>
  );
}
